import styled, { keyframes } from "styled-components";
import { Link as LinkScroll } from "react-scroll";

export const HeroContainer = styled.div`
  padding: 5rem 1.5rem 3rem;
  margin-right: auto;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  min-height: 88vh;
  position: relative;
  z-index: 1;

  @media (min-width: 576px) {
    max-width: 540px;
  }
  @media (min-width: 768px) {
    max-width: 720px;
    padding-top: 6rem;
  }
  @media (min-width: 992px) {
    max-width: 960px;
  }
  @media (min-width: 1200px) {
    max-width: 1140px;
  }
  @media (min-width: 1400px) {
    max-width: 1320px;
  }
`;

export const BackgroundGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at top left, rgba(23, 42, 65, 0.8), transparent 40%),
              radial-gradient(circle at bottom right, rgba(28, 37, 65, 0.8), transparent 40%);
  z-index: -1;
`;

export const HeroWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2rem;
  margin-bottom: 4rem;

  @media screen and (max-width: 992px) {
    flex-direction: column;
    text-align: center;
    margin-bottom: 2rem;
  }
`;

export const HeroLeft = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  text-align: left;
  flex: 1;
  position: relative;
  z-index: 2;

  h1 {
    font-size: 3.5rem;
    background: linear-gradient(to right, #f6f6f6, #a5b4fc);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-weight: 700;
    margin-bottom: 0.5rem;
    letter-spacing: -0.02em;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -0.5rem;
      width: 3rem;
      height: 0.25rem;
      background: linear-gradient(to right, #4f46e5, #818cf8);
      border-radius: 4px;
    }
  }

  h5 {
    font-size: 1.75rem;
    background: linear-gradient(to right, #c7d2fe, #818cf8);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 1.5rem;
    font-weight: 500;
    min-height: 5rem;
  }

  @media screen and (max-width: 992px) {
    text-align: center;
    align-items: center;
    margin-bottom: 2rem;

    h1 {
      font-size: 2.75rem;
      &::after {
        left: 50%;
        transform: translateX(-50%);
      }
    }

    h5 {
      font-size: 1.5rem;
      min-height: 4rem;
    }
  }

  @media screen and (max-width: 576px) {
    h1 {
      font-size: 2.25rem;
    }
    
    h5 {
      font-size: 1.25rem;
    }
  }
`;

export const HeroRight = styled.div`
  flex: 1;
  justify-content: center;
  display: flex;
  position: relative;
  z-index: 1;
`;

export const ImageContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  
  &::before {
    content: '';
    position: absolute;
    top: -10%;
    left: -10%;
    width: 120%;
    height: 120%;
    background: radial-gradient(circle, rgba(99, 102, 241, 0.15), transparent 70%);
    border-radius: 50%;
    z-index: -1;
  }

  img {
    max-width: 100%;
    height: auto;
    max-height: 400px;
    filter: drop-shadow(0 0 20px rgba(99, 102, 241, 0.5));
    transition: transform 0.5s ease;
    
    &:hover {
      transform: scale(1.03) translateY(-5px);
    }
  }

  @media screen and (max-width: 768px) {
    img {
      max-height: 320px;
    }
  }
`;

export const SocialIcons = styled.div`
  display: flex;
  margin-top: 2rem;
  gap: 1rem;

  @media screen and (max-width: 992px) {
    justify-content: center;
  }
`;

export const SocialIconLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #f6f6f6;
  font-size: 1.5rem;
  width: 3rem;
  height: 3rem;
  background: rgba(99, 102, 241, 0.2);
  border-radius: 50%;
  transition: all 0.3s ease-in-out;
  border: 1px solid rgba(99, 102, 241, 0.3);
  
  &:hover {
    background: rgba(99, 102, 241, 0.4);
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
  }
`;

export const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
  
  @media screen and (max-width: 992px) {
    justify-content: center;
  }
`;

export const TechBadge = styled.span`
  padding: 0.5rem 1rem;
  background: rgba(17, 24, 39, 0.8);
  color: ${props => props.color || '#f6f6f6'};
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid ${props => props.color || 'rgba(99, 102, 241, 0.3)'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '';
    display: inline-block;
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background-color: ${props => props.color || '#f6f6f6'};
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-15px);
  }
  60% {
    transform: translateY(-7px);
  }
`;

export const ScrollDown = styled(LinkScroll)`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-top: 2rem;
  position: relative;
  z-index: 10;
  
  animation: ${bounce} 2.5s infinite;

  @media screen and (max-width: 992px) {
    margin-top: 3rem;
  }
`;

export const ScrollLink = styled.div`
  display: flex;
  align-items: center;
  color: #f6f6f6;
  font-size: 1.25rem;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(to right, rgba(79, 70, 229, 0.8), rgba(99, 102, 241, 0.6));
  border-radius: 50px;
  transition: all 0.3s ease;
  border: 1px solid rgba(129, 140, 248, 0.5);
  
  .scroll-icon {
    margin-left: 0.5rem;
    animation: ${bounce} 2s infinite;
  }
  
  &:hover {
    background: linear-gradient(to right, rgba(79, 70, 229, 1), rgba(99, 102, 241, 0.8));
    box-shadow: 0 8px 16px rgba(79, 70, 229, 0.3);
    transform: translateY(-2px);
  }
`;